import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AnimalSilhouette } from './AnimalSilhouette';
import { StateBadge } from './StateBadge';
import { GRID } from '@/constants/testIds';
import { ArrowRight } from 'lucide-react';

export function AnimalCard({ animal, onSponsor }) {
  const isAvailable = animal.state === 'available';
  const isReserved = animal.state === 'reserved';
  const isSponsored = animal.state === 'sponsored';
  const cover = animal.cover;

  return (
    <article
      data-testid={GRID.card}
      className="group relative rounded-xl bg-white border border-border card-shadow card-shadow-hover overflow-hidden flex flex-col transition-shadow"
    >
      <Link to={`/animal/${animal.slug || animal.id}`} className="block" aria-label={`View ${animal.name}`}>
        <div className="relative aspect-[4/3] bg-[#EEF0EE] overflow-hidden">
          {cover ? (
            <img src={cover} alt={`${animal.name}, ${animal.category}`} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <AnimalSilhouette category={animal.category} />
          )}
        </div>
      </Link>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 data-testid={GRID.cardName} className="text-xl font-extrabold leading-tight text-[hsl(var(--foreground))] truncate">
              <Link to={`/animal/${animal.slug || animal.id}`}>{animal.name}</Link>
            </h3>
            <p className="text-xs font-semibold text-muted-foreground mt-0.5">
              {animal.category === 'horse' ? 'Horse' : 'Pony'}{animal.breed ? ` · ${animal.breed}` : ''}
            </p>
          </div>
          <StateBadge state={animal.state} />
        </div>
        {animal.one_liner ? (
          <p className="text-[15px] leading-relaxed text-[#3E4A44] line-clamp-3">{animal.one_liner}</p>
        ) : null}
        {isSponsored && animal.sponsor_name && (
          <p className="text-xs font-semibold text-[#384540]">Sponsored by {animal.sponsor_name}</p>
        )}
        <div className="mt-auto pt-2">
          {isAvailable && (
            <Button
              data-testid={GRID.cardPrimaryCta}
              onClick={() => onSponsor?.(animal)}
              className="btn w-full rounded-lg bg-[#1E3A2F] hover:bg-[#16302A] text-white font-bold text-[15px] h-12"
            >
              Sponsor {animal.name}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}
          {isReserved && (
            <div
              data-testid={GRID.cardPrimaryCta}
              role="status"
              className="w-full h-12 flex items-center justify-center text-[13px] font-semibold text-[#6B7370] bg-transparent border-t border-border"
            >
              Currently on hold
            </div>
          )}
          {isSponsored && (
            <Button
              data-testid={GRID.cardPrimaryCta}
              variant="outline"
              asChild
              className="btn w-full rounded-lg h-12 border-border text-[#1E3A2F] hover:bg-[#F7F8F7] font-bold"
            >
              <a href="#the-herd">Meet the rest of the herd</a>
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
